import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import type { Theme } from '@mui/material/styles';

import {
  FIELD_BORDER_WIDTH_PX,
  FIELD_PADDING_PX,
  FIELD_RADIUS_PX,
  FIELD_VALUE_TYPOGRAPHY,
  fieldInputPadding,
} from 'theme/components/field-geometry';
import { focusRing, sizeVariants } from 'theme/theme.helpers';
import type { ComponentOverrides } from 'theme/theme.types';

const FOCUS_RING_WIDTH_PX = 2;
const FORCED_COLORS_FOCUS_OUTLINE = `${FOCUS_RING_WIDTH_PX}px solid transparent`;

const OUTLINE = `.${outlinedInputClasses.notchedOutline}`;
const FOCUSED = `.${outlinedInputClasses.focused}`;
const ERROR = `.${outlinedInputClasses.error}`;
const DISABLED = `.${outlinedInputClasses.disabled}`;
const MULTILINE = `.${outlinedInputClasses.multiline}`;

interface OutlineState {
  disabled?: boolean;
  error?: boolean;
  focused?: boolean;
}

const outlineColor = (theme: Theme, state: OutlineState) => {
  if (state.disabled) return theme.palette.action.disabledBorder;
  if (state.error) return theme.palette.error.main;
  if (state.focused) return theme.palette.secondary.main;

  return theme.palette.tokens.navy[400];
};

export const MuiOutlinedInput: ComponentOverrides['MuiOutlinedInput'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      ...theme.typography[FIELD_VALUE_TYPOGRAPHY.medium],
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      borderRadius: FIELD_RADIUS_PX,
      [`&${MULTILINE}`]: { padding: 0 },
      [`& ${OUTLINE}`]: {
        borderWidth: FIELD_BORDER_WIDTH_PX,
        borderColor: outlineColor(theme, {}),
      },
      [`&:hover ${OUTLINE}`]: { borderColor: theme.palette.secondary.main },
      [`&${FOCUSED}`]: {
        outline: FORCED_COLORS_FOCUS_OUTLINE,
        boxShadow: focusRing(
          theme.palette.focusRing.secondary.focused,
          FOCUS_RING_WIDTH_PX
        ),
      },
      [`&${FOCUSED} ${OUTLINE}`]: {
        borderWidth: FIELD_BORDER_WIDTH_PX,
        borderColor: outlineColor(theme, { focused: true }),
      },
      [`&${ERROR} ${OUTLINE}, &${ERROR}:hover ${OUTLINE}`]: {
        borderColor: outlineColor(theme, { error: true }),
      },
      [`&${ERROR}${FOCUSED}`]: {
        boxShadow: focusRing(
          theme.palette.focusRing.error.focused,
          FOCUS_RING_WIDTH_PX
        ),
      },
      [`&${DISABLED}`]: {
        backgroundColor: theme.palette.action.disabledBackground,
        cursor: 'not-allowed',
      },
      [`&${DISABLED} ${OUTLINE}, &${DISABLED}:hover ${OUTLINE}`]: {
        borderColor: outlineColor(theme, { disabled: true }),
      },
      variants: sizeVariants(FIELD_PADDING_PX, (size) => ({
        ...theme.typography[FIELD_VALUE_TYPOGRAPHY[size]],
      })),
    }),
    notchedOutline: ({ theme, ownerState }) => ({
      borderWidth: FIELD_BORDER_WIDTH_PX,
      borderColor: outlineColor(theme, ownerState),
    }),
    input: ({ theme }) => ({
      boxSizing: 'border-box',
      height: 'auto',
      ...fieldInputPadding('medium'),
      '&::placeholder': { color: theme.palette.text.disabled, opacity: 1 },
      [`&${DISABLED}`]: {
        WebkitTextFillColor: theme.palette.text.primary,
        cursor: 'not-allowed',
      },
      variants: sizeVariants(FIELD_PADDING_PX, fieldInputPadding),
    }),
  },
};
